import {
  ApiError,
  MeetingLimitedResponse,
  Auth0AccountRole,
  ParentLimitedResponse,
  MeetingsService,
  ExportsService,
} from "client/openapi";
import { Button, ButtonColor, ButtonFill, ButtonSize } from "components/Button";
import { getButtonRoleColor } from "util/contextColor";
import Notifications from "util/notifications";
import moment from "moment";
import { saveAs } from "file-saver";
import { APIResponse, PageStatus } from "types";
import { CountdownTimerIcon, DownloadIcon } from "@radix-ui/react-icons";
import { returnSubjectName } from "util/concatenateSubject";
import "../index.css";
import {
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import { Dialog } from "components/Dialog";
import MeetingDialog from "components/MeetingDialog";
import { DialogTrigger } from "components/Dialog";
import { IoEyeSharp } from "react-icons/io5";
import { getAttendees } from "util/getAttendees";
import { useInfiniteMeetings } from "util/useInfiniteMeetings";
import SectionTitle from "components/SectionTitle";
import { BubbleTone } from "components/IconBubble";

const SCROLL_PREFETCH_PX = 400;

export default function MeetingHistory({
  parent,
  exportingMeetings,
  setExportingMeetings,
  setStatus,
  setError,
}: {
  parent: ParentLimitedResponse;
  exportingMeetings: boolean;
  setExportingMeetings: (boolean) => void;
  setStatus: (PageStatus) => void;
  setError: (APIResponse) => void;
}) {
  const { currently_selected_role } = useContext(OrgRolesAndAccountContext);

  const fetchFn = useCallback(
    (start: string, until: string) =>
      MeetingsService.getMeetingsByParent({
        parentId: parent.id,
        start,
        until,
      }) as Promise<MeetingLimitedResponse[]>,
    [parent.id]
  );

  const {
    meetings,
    loadMorePast,
    loadMoreFuture,
    loadingPast,
    loadingFuture,
    reachedPast,
    reachedFuture,
    initialLoading,
  } = useInfiniteMeetings<MeetingLimitedResponse>(fetchFn);

  const sortedMeetings = useMemo(
    () =>
      [...meetings].sort(
        (a, b) => moment(b.start).valueOf() - moment(a.start).valueOf()
      ),
    [meetings]
  );
  const endOfToday = moment().endOf("day");
  const todayBoundaryIndex = sortedMeetings.findIndex((m) =>
    moment(m.start).isSameOrBefore(endOfToday)
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const todayRef = useRef<HTMLDivElement>(null);
  const initialScrollDoneRef = useRef(false);
  const futureLoadAnchorRef = useRef<{
    scrollTop: number;
    scrollHeight: number;
  } | null>(null);

  useLayoutEffect(() => {
    if (initialScrollDoneRef.current) return;
    if (sortedMeetings.length === 0) return;
    if (containerRef.current && todayRef.current) {
      containerRef.current.scrollTop =
        todayRef.current.offsetTop - containerRef.current.offsetTop;
      initialScrollDoneRef.current = true;
    } else if (containerRef.current && todayBoundaryIndex === -1) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
      initialScrollDoneRef.current = true;
    }
  }, [sortedMeetings.length, todayBoundaryIndex]);

  useLayoutEffect(() => {
    if (futureLoadAnchorRef.current && containerRef.current) {
      const anchor = futureLoadAnchorRef.current;
      const newScrollHeight = containerRef.current.scrollHeight;
      if (newScrollHeight !== anchor.scrollHeight) {
        containerRef.current.scrollTop =
          anchor.scrollTop + (newScrollHeight - anchor.scrollHeight);
        futureLoadAnchorRef.current = null;
      }
    }
  }, [sortedMeetings.length]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollTop < SCROLL_PREFETCH_PX && !loadingFuture && !reachedFuture) {
      futureLoadAnchorRef.current = {
        scrollTop: el.scrollTop,
        scrollHeight: el.scrollHeight,
      };
      loadMoreFuture();
    }
    if (
      el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_PREFETCH_PX &&
      !loadingPast &&
      !reachedPast
    ) {
      loadMorePast();
    }
  };

  async function exportParentMeetings() {
    if (!parent) {
      return Notifications.error("Unable to export meeting data.");
    }

    setExportingMeetings(true);

    await ExportsService.exportParentMeetings({
      parentId: parent.id,
      until: moment().toISOString(),
    })
      .then((data) => {
        try {
          const blob = new Blob([data], { type: "text/csv" });
          const fileName = "export.csv";
          saveAs(blob, fileName);
          Notifications.success(
            `${parent.first_name}'s meeting export has been saved to your computer!`
          );
        } catch (error) {
          console.error(error);
          Notifications.error("Unable to save file.");
        }
      })
      .catch((e: ApiError) => {
        setStatus(PageStatus.ERROR);
        setError({ message: "Unable to export parent's meetings" });
        console.error(`Error (#${e.status}): ${e.message}`);
      });

    setExportingMeetings(false);
  }

  return (
    <section className="student-profile--section" id="student-meeting-history">
      <div className="student-profile--section-header">
        <SectionTitle icon={<CountdownTimerIcon />} tone={BubbleTone.VIOLET}>
          Meeting History
        </SectionTitle>

        <div className="flex flex-row gap-1.5 items-center">
          {meetings.length > 0 && (
            <Button
              color={getButtonRoleColor(
                currently_selected_role
                  ? currently_selected_role
                  : Auth0AccountRole.ME
              )}
              size={ButtonSize.SMALL}
              onClick={() => exportParentMeetings()}
              disabled={exportingMeetings}
            >
              Export{" "}
              {exportingMeetings ? (
                <span
                  className="spinner-border spinner-border-square"
                  role="status"
                  aria-hidden="true"
                ></span>
              ) : (
                <DownloadIcon aria-label="Export Meetings" />
              )}
            </Button>
          )}
        </div>
      </div>

      <div
        className="student-profile--section-items"
        ref={containerRef}
        onScroll={handleScroll}
      >
        {loadingFuture && (
          <div className="student-profile--section-item flex justify-center py-2 text-sm text-gray-500">
            <span
              className="spinner-border spinner-border-square mr-2"
              role="status"
              aria-hidden="true"
            ></span>
            Loading more meetings...
          </div>
        )}
        {initialLoading ? (
          <div className="student-profile--section-item flex justify-center py-4 text-sm text-gray-500">
            <span
              className="spinner-border spinner-border-square mr-2"
              role="status"
              aria-hidden="true"
            ></span>
            Loading meetings...
          </div>
        ) : sortedMeetings.length > 0 ? (
          sortedMeetings.map((m, i) => {
            const isFuture = moment(m.start).isAfter(endOfToday);
            return (
              <div
                key={m.id}
                ref={i === todayBoundaryIndex ? todayRef : undefined}
                className="student-profile--section-item student-detail--historical-meeting pl-4 py-4"
                style={{
                  borderLeft: isFuture
                    ? "4px solid #4ade80"
                    : "4px solid #3cb8e3",
                  backgroundColor: isFuture ? "#dcfce7" : "#edf7fd",
                }}
              >
                <div className="student-detail--historical-meeting--details">
                  <p>
                    <b>{moment(m.start).format("ddd, MMM D, YYYY")}</b> &middot;{" "}
                    {moment(m.start).format("h:mma")} -{" "}
                    {moment(m.start).add(m.duration, "minutes").format("h:mma")}{" "}
                    &middot;{" "}
                    <LazyMeetingDialog
                      meeting={m}
                      role={currently_selected_role as Auth0AccountRole}
                    />
                  </p>
                  <p>
                    {returnSubjectName(m.org_subject)} &middot;{" "}
                    {getAttendees(Auth0AccountRole.ORG_TUTOR, m)} &middot;{" "}
                    {m.location?.name || m.other_location}
                  </p>
                </div>
              </div>
            );
          })
        ) : (
          <div className="student-profile--section-item">No meetings.</div>
        )}
        {loadingPast && (
          <div className="student-profile--section-item flex justify-center py-2 text-sm text-gray-500">
            <span
              className="spinner-border spinner-border-square mr-2"
              role="status"
              aria-hidden="true"
            ></span>
            Loading more meetings...
          </div>
        )}
      </div>
    </section>
  );
}

function LazyMeetingDialog({
  meeting,
  role,
}: {
  meeting: MeetingLimitedResponse;
  role: Auth0AccountRole;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger>
        <Button
          size={ButtonSize.EXTRA_SMALL}
          color={ButtonColor.DARK_BLUE}
          fill={ButtonFill.HOLLOW}
        >
          <IoEyeSharp />
        </Button>
      </DialogTrigger>
      {open && (
        <MeetingDialog
          isCreate={false}
          role={role}
          event={meeting}
          setEvents={() => Promise.resolve()}
        />
      )}
    </Dialog>
  );
}
